import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  Tag, 
  Plus, 
  Trash2, 
  Check, 
  X, 
  Calendar, 
  Percent, 
  Sparkles, 
  AlertCircle,
  Copy
} from 'lucide-react';

export default function AdminCoupons() {
  const { coupons, addCoupon, deleteCoupon, toggleCouponStatus, addToast } = useShop();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState({
    code: '',
    discountPercent: 10,
    minOrder: 10000,
    description: '',
    expiresAt: '2026-12-31'
  });

  const handleCreateCoupon = (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      addToast('Please enter a coupon code', 'error');
      return;
    }

    addCoupon({
      code: form.code.trim().toUpperCase(),
      discountPercent: Number(form.discountPercent),
      minOrder: Number(form.minOrder),
      description: form.description.trim() || `${form.discountPercent}% Off Royal Privilege Code`,
      expiresAt: form.expiresAt,
      isActive: true
    });

    setIsModalOpen(false);
    setForm({
      code: '',
      discountPercent: 10,
      minOrder: 10000,
      description: '',
      expiresAt: '2026-12-31'
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    addToast(`Coupon ${code} copied to clipboard!`, 'info');
  };

  return (
    <div>
      {/* Top Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>
            <Sparkles size={13} /> Promotions & Royal Privileges
          </div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
            Discount <span className="text-gold-gradient">Coupons Manager</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Configure festive concession codes and promotional discounts synced with storefront checkout.
          </p>
        </div>

        <button
          className="btn btn-gold btn-sm"
          onClick={() => setIsModalOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.82rem' }}
        >
          <Plus size={16} />
          <span>Create New Coupon</span>
        </button>
      </div>

      {/* Coupons Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1.25rem'
      }}>
        {coupons.map((coupon) => (
          <div
            key={coupon.id || coupon.code}
            style={{
              background: '#13131e',
              border: coupon.isActive ? '1px solid var(--border-gold)' : '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              opacity: coupon.isActive ? 1 : 0.65
            }}
          >
            <div>
              {/* Header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div style={{
                    padding: '0.45rem 0.8rem',
                    background: 'rgba(212, 175, 55, 0.15)',
                    border: '1px dashed var(--gold-400)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'monospace',
                    fontWeight: 800,
                    fontSize: '1.05rem',
                    color: 'var(--gold-300)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}>
                    <Tag size={14} />
                    <span>{coupon.code}</span>
                  </div>
                  <button
                    onClick={() => copyCode(coupon.code)}
                    title="Copy Code"
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.2rem' }}
                  >
                    <Copy size={15} />
                  </button>
                </div>

                <span style={{
                  padding: '0.2rem 0.65rem',
                  borderRadius: '999px',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  background: coupon.isActive ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  color: coupon.isActive ? '#34d399' : '#f87171',
                  border: `1px solid ${coupon.isActive ? 'rgba(52, 211, 153, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                }}>
                  {coupon.isActive ? 'Active' : 'Disabled'}
                </span>
              </div>

              {/* Discount Details */}
              <div style={{ marginBottom: '0.8rem' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span>{coupon.discountPercent}% OFF</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.3rem' }}>
                  {coupon.description}
                </div>
              </div>

              {/* Min Order & Expiry */}
              <div style={{
                background: '#0c0c12',
                borderRadius: 'var(--radius-sm)',
                padding: '0.6rem 0.8rem',
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                marginBottom: '1rem'
              }}>
                <div><strong>Min Order Value:</strong> {coupon.minOrder ? formatINR(coupon.minOrder) : 'No Minimum'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Calendar size={12} color="var(--gold-400)" />
                  <span>Valid Until: {coupon.expiresAt || '31 Dec 2026'}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '0.8rem',
              borderTop: '1px solid rgba(255, 255, 255, 0.06)'
            }}>
              <button
                onClick={() => toggleCouponStatus(coupon.id)}
                className="btn btn-glass btn-sm"
                style={{ fontSize: '0.74rem' }}
              >
                {coupon.isActive ? 'Deactivate' : 'Activate'}
              </button>

              <button
                onClick={() => deleteCoupon(coupon.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f87171',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.74rem'
                }}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Coupon Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1.5rem'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '480px',
            backgroundColor: '#13131e',
            border: '1px solid var(--border-gold)',
            borderRadius: 'var(--radius-md)',
            padding: '2rem',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff' }}>
                  Create Privilege Promo Code
                </h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Give your VIP clientele instant cart concessions.
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateCoupon} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Promo Code * (e.g. FESTIVE20)
                </label>
                <input
                  type="text"
                  required
                  placeholder="DIWALI2026"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="input-luxury"
                  style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 800 }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Discount Percentage (%) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    required
                    value={form.discountPercent}
                    onChange={(e) => setForm({ ...form, discountPercent: e.target.value })}
                    className="input-luxury"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Min Order Value (₹)
                  </label>
                  <input
                    type="number"
                    step="1000"
                    value={form.minOrder}
                    onChange={(e) => setForm({ ...form, minOrder: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Description / Marketing Note
                </label>
                <input
                  type="text"
                  placeholder="Special Dhanteras offer for orders above ₹25,000"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-luxury"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={form.expiresAt}
                  onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                  className="input-luxury"
                />
              </div>

              <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn btn-glass"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-gold"
                  style={{ flex: 1 }}
                >
                  Publish Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
